import * as THREE from 'three'

import PlayerModel, { type WeaponKind } from '../player/PlayerModel'
import { DUCK_TIME } from '../player/movement'
import type { NetEvent, PlayerSnapshot } from '@/net/protocol'

interface RemoteState {
  model: PlayerModel
  lastShotAt: number
  lastReloadAt: number
  wasReloading: boolean
  yaw: number
  duckT: number // smoothed crouch amount (0..1), like the local player
  // Death is started by EITHER the death event (instant) OR the snapshot's
  // alive=false flag (reliable). `seenDeadSnapshot` records that the death has
  // propagated into the interpolated snapshot, so a later alive=true is a real
  // respawn rather than the ~100ms interpolation lag right after the event —
  // without it the corpse re-triggers every respawn and the fall loops.
  dying: boolean
  deathAt: number
  deathVariant: number
  seenDeadSnapshot: boolean
  readonly deathPos: THREE.Vector3
}

// Match the local player's collapse timing (PlayerController.beginLocalDeath).
const DEATH_FALL_TIME = 0.9
// If we never saw the death in a snapshot (e.g. a fast death+respawn batched in
// one frame), clear the corpse this long after it started so it can't get stuck.
const RESPAWN_GRACE = 0.5
// Safety net: drop a corpse if its owner disconnected mid-collapse.
const CORPSE_MAX_AGE = 8.0
// event.point is the body-height hit location; drop to feet when no snapshot is
// available to read the true ground height from.
const HIT_TO_FOOT = 36

export default class RemotePlayers {
  private readonly remotes = new Map<string, RemoteState>()
  private readonly feet = new THREE.Vector3()
  private lastTime = -1

  private create(): RemoteState {
    const model = new PlayerModel()
    model.setVisible(true)
    return {
      model,
      lastShotAt: -Infinity,
      lastReloadAt: -Infinity,
      wasReloading: false,
      yaw: 0,
      duckT: 0,
      dying: false,
      deathAt: -Infinity,
      deathVariant: 0,
      seenDeadSnapshot: false,
      deathPos: new THREE.Vector3(),
    }
  }

  private ensure(id: string): RemoteState {
    let remote = this.remotes.get(id)
    if (!remote) {
      remote = this.create()
      this.remotes.set(id, remote)
    }
    return remote
  }

  /** Lay a player down: pick a death variant, freeze the corpse at the death
   * spot, and skin the weapon they were holding. */
  private startCorpse(
    remote: RemoteState,
    time: number,
    x: number,
    y: number,
    z: number,
    yaw: number,
    weapon: WeaponKind,
    seenDeadSnapshot: boolean,
  ): void {
    remote.dying = true
    remote.deathAt = time
    remote.deathVariant = Math.floor(Math.random() * 8)
    remote.deathPos.set(x, y, z)
    remote.yaw = yaw
    remote.seenDeadSnapshot = seenDeadSnapshot
    remote.model.setWeapon(weapon)
    remote.model.setVisible(true)
  }

  public update(
    players: PlayerSnapshot[],
    time: number,
    events: NetEvent[],
    selfId: string,
  ): void {
    const dt = this.lastTime < 0 ? 0 : Math.max(0, Math.min(0.1, time - this.lastTime))
    this.lastTime = time
    const byId = new Map(players.map((player) => [player.id, player]))

    // Instant death from the event: it arrives ~100ms before the snapshot's
    // alive flag flips, and carries the server-accurate hit location.
    for (const event of events) {
      if (event.type !== 'death') continue
      // The local player draws its own death/corpse in PlayerController; never
      // mirror self here, or the player sees a duplicate body next to theirs.
      if (event.playerId === selfId) continue
      const remote = this.ensure(event.playerId)
      if (remote.dying) continue
      const snap = byId.get(event.playerId)
      this.startCorpse(
        remote,
        time,
        event.point.x,
        snap ? snap.pos.y : event.point.y - HIT_TO_FOOT,
        event.point.z,
        snap ? snap.yaw : remote.yaw,
        (snap?.weapon ?? 'ak') as WeaponKind,
        false,
      )
    }

    // Drive each player off the authoritative snapshot alive flag.
    for (const player of players) {
      const remote = this.ensure(player.id)

      if (!player.alive) {
        if (!remote.dying) {
          // The death event was dropped — lay them down from the snapshot.
          this.startCorpse(
            remote,
            time,
            player.pos.x,
            player.pos.y,
            player.pos.z,
            player.yaw,
            player.weapon as WeaponKind,
            true,
          )
        } else {
          remote.seenDeadSnapshot = true // death has reached the snapshot
        }
        continue
      }

      // alive === true: revive only on a genuine respawn, never on the lag
      // window right after a death event (else the collapse restarts in a loop).
      if (remote.dying) {
        if (remote.seenDeadSnapshot || time - remote.deathAt >= RESPAWN_GRACE) {
          remote.dying = false
        } else {
          continue
        }
      }
      this.poseAlive(remote, player, time, dt, events)
    }

    // Corpses keep playing the collapse (frozen at the last frame); everyone
    // else that left the snapshot (disconnected) is disposed.
    for (const [id, remote] of this.remotes) {
      if (remote.dying) {
        const elapsed = time - remote.deathAt
        if (elapsed > CORPSE_MAX_AGE) {
          remote.model.dispose()
          this.remotes.delete(id)
          continue
        }
        const t = Math.min(0.98, elapsed / DEATH_FALL_TIME)
        remote.model.setPose(
          remote.deathPos,
          remote.yaw,
          0,
          0,
          0,
          time,
          'death',
          remote.deathVariant + t,
        )
        remote.model.setVisible(true)
        continue
      }
      if (!byId.has(id)) {
        remote.model.dispose()
        this.remotes.delete(id)
      }
    }
  }

  private poseAlive(
    remote: RemoteState,
    player: PlayerSnapshot,
    time: number,
    dt: number,
    events: NetEvent[],
  ): void {
    remote.yaw = player.yaw

    // Smoothly crouch/stand toward the synced duck state (mirrors the local
    // player's DUCK_TIME ramp) so remotes visibly duck instead of snapping.
    const targetDuck = player.ducking ? 1 : 0
    const step = DUCK_TIME > 0 ? dt / DUCK_TIME : 1
    remote.duckT = THREE.MathUtils.clamp(
      remote.duckT + THREE.MathUtils.clamp(targetDuck - remote.duckT, -step, step),
      0,
      1,
    )

    remote.model.setWeapon(player.weapon as WeaponKind)
    const speed01 = Math.min(Math.hypot(player.vel.x, player.vel.z) / 221, 1)
    const shotEvent = events.find(
      (event) => event.type === 'shot' && event.shooterId === player.id,
    )
    const reloadEvent = events.find(
      (event) => event.type === 'reload' && event.playerId === player.id,
    )
    if (shotEvent) remote.lastShotAt = time
    if (reloadEvent || (player.reloading && !remote.wasReloading)) {
      remote.lastReloadAt = time
    }
    remote.wasReloading = player.reloading

    let action: 'aim' | 'shoot' | 'reload' = 'aim'
    let actionT = 0
    if (player.reloading) {
      action = 'reload'
      actionT = (time - remote.lastReloadAt) / 3.03
    } else if (time - remote.lastShotAt < 0.25) {
      action = 'shoot'
      actionT = (time - remote.lastShotAt) / 0.25
    }

    this.feet.set(player.pos.x, player.pos.y, player.pos.z)
    remote.model.setPose(
      this.feet,
      player.yaw,
      player.pitch,
      remote.duckT,
      speed01,
      time,
      action,
      Math.max(0, Math.min(1, actionT)),
    )
    remote.model.group.rotation.x = 0
    remote.model.group.rotation.z = 0
  }

  public dispose(): void {
    for (const remote of this.remotes.values()) remote.model.dispose()
    this.remotes.clear()
  }
}
